// src/pages/FirebaseTest.js
// Development-only page for testing Firebase connection
// Access at /firebase-test (remove before production)

import React, { useState } from 'react';
import { runFirebaseTests, quickConnectionCheck, getFirebaseConfig } from '../utils/firebaseTest';

const FirebaseTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [quickCheck, setQuickCheck] = useState(null);

  const handleRunTests = async () => {
    setIsRunning(true);
    setTestResults(null);
    console.log('🚀 Starting Firebase connection tests...');

    try {
      const results = await runFirebaseTests();
      setTestResults(results);
    } catch (error) {
      setTestResults({
        overall: {
          success: false,
          message: `Error running tests: ${error.message}`
        },
        error: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleQuickCheck = () => {
    const check = quickConnectionCheck();
    setQuickCheck(check);
    console.log('Quick check results:', check);
  };

  const handleShowConfig = () => {
    const config = getFirebaseConfig();
    console.log('Firebase config:', config);
    alert(JSON.stringify(config, null, 2));
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const getStatusColor = (success) => {
    return success ? '#10b981' : '#ef4444';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FAFAF9 0%, #F5F0EB 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1F2937',
            marginBottom: '8px'
          }}>
            Firebase Connection Tests
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            marginBottom: '24px'
          }}>
            Test Firebase Auth, Firestore, and Storage connections
          </p>

          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleRunTests}
              disabled={isRunning}
              style={{
                padding: '10px 24px',
                backgroundColor: isRunning ? '#9CA3AF' : '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isRunning ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isRunning ? '🔄 Running Tests...' : '🧪 Run Full Test Suite'}
            </button>

            <button
              onClick={handleQuickCheck}
              style={{
                padding: '10px 24px',
                backgroundColor: 'white',
                color: '#7c3aed',
                border: '2px solid #7c3aed',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              ⚡ Quick Check
            </button>

            <button
              onClick={handleShowConfig}
              style={{
                padding: '10px 24px',
                backgroundColor: 'white',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              ⚙️ Show Config
            </button>
          </div>
        </div>

        {/* Quick Check Results */}
        {quickCheck && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: `4px solid ${getStatusColor(quickCheck.success)}`
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px'
            }}>
              {getStatusIcon(quickCheck.success)} Quick Check
            </h2>
            <div style={{ fontSize: '14px', color: '#4B5563' }}>
              <p style={{ marginBottom: '8px' }}>
                <strong>Status:</strong> {quickCheck.message}
              </p>
              <p style={{ marginBottom: '8px' }}>
                <strong>Initialized:</strong> {quickCheck.initialized ? 'Yes' : 'No'}
              </p>
              <div style={{ marginTop: '12px' }}>
                <strong>Services:</strong>
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li>Auth: {quickCheck.services.auth ? '✅' : '❌'}</li>
                  <li>Firestore: {quickCheck.services.firestore ? '✅' : '❌'}</li>
                  <li>Storage: {quickCheck.services.storage ? '✅' : '❌'}</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Full Test Results */}
        {testResults && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {/* Overall Result */}
            <div style={{
              padding: '20px',
              borderRadius: '8px',
              background: testResults.overall.success ? '#f0fdf4' : '#fef2f2',
              border: `2px solid ${getStatusColor(testResults.overall.success)}`,
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: testResults.overall.success ? '#059669' : '#dc2626',
                marginBottom: '8px'
              }}>
                {getStatusIcon(testResults.overall.success)} Overall Result
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#4B5563'
              }}>
                {testResults.overall.message}
              </p>
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                marginTop: '8px'
              }}>
                Test run at: {new Date(testResults.timestamp).toLocaleString()}
              </p>
            </div>

            {/* Individual Test Results */}
            <div style={{ display: 'grid', gap: '16px' }}>
              {/* Initialization */}
              <TestResultCard
                title="1. Firebase Initialization"
                result={testResults.initialization}
              />

              {/* Auth */}
              <TestResultCard
                title="2. Firebase Auth"
                result={testResults.auth}
              />

              {/* Firestore */}
              <TestResultCard
                title="3. Firestore Database"
                result={testResults.firestore}
              />

              {/* Storage */}
              <TestResultCard
                title="4. Firebase Storage"
                result={testResults.storage}
              />
            </div>

            {/* Next Steps */}
            {testResults.overall.success && (
              <div style={{
                marginTop: '24px',
                padding: '20px',
                background: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '8px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '12px'
                }}>
                  ✅ Next Steps
                </h3>
                <ol style={{
                  fontSize: '14px',
                  color: '#1e3a8a',
                  paddingLeft: '20px',
                  lineHeight: '1.6'
                }}>
                  <li>Open <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px' }}>src/services/auth-service.js</code></li>
                  <li>Change <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px' }}>USE_FIREBASE = false</code> to <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px' }}>USE_FIREBASE = true</code></li>
                  <li>Restart dev server: <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px' }}>npm start</code></li>
                  <li>Test login/signup with Firebase Auth</li>
                  <li>Update Firestore security rules in Firebase Console</li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!testResults && !isRunning && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '16px'
            }}>
              What This Tests
            </h2>
            <ul style={{
              fontSize: '14px',
              color: '#4B5563',
              lineHeight: '1.8',
              paddingLeft: '20px'
            }}>
              <li><strong>Initialization:</strong> Checks if Firebase SDK is properly initialized</li>
              <li><strong>Auth:</strong> Tests anonymous sign-in (doesn't require credentials)</li>
              <li><strong>Firestore:</strong> Tests write, read, and delete operations</li>
              <li><strong>Storage:</strong> Tests file upload, URL generation, and deletion</li>
            </ul>

            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#fffbeb',
              border: '1px solid #fef3c7',
              borderRadius: '8px'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#92400e',
                marginBottom: '8px'
              }}>
                <strong>⚠️ Before Running:</strong>
              </p>
              <p style={{
                fontSize: '14px',
                color: '#78350f',
                lineHeight: '1.6'
              }}>
                Make sure you've set up Firestore security rules in the Firebase Console.
                The test will create temporary documents in <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '4px' }}>_connection_tests</code> collection
                and temporary files in <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: '4px' }}>_connection_tests/</code> storage folder.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component for individual test result cards
const TestResultCard = ({ title, result }) => {
  const getStatusIcon = (success) => success ? '✅' : '❌';
  const getStatusColor = (success) => success ? '#10b981' : '#ef4444';

  return (
    <div style={{
      padding: '16px',
      border: '1px solid #E5E7EB',
      borderLeft: `4px solid ${getStatusColor(result.success)}`,
      borderRadius: '8px',
      background: '#FAFAFA'
    }}>
      <h3 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1F2937',
        marginBottom: '8px'
      }}>
        {getStatusIcon(result.success)} {title}
      </h3>
      <p style={{
        fontSize: '14px',
        color: '#4B5563',
        marginBottom: '12px'
      }}>
        {result.message}
      </p>
      {result.details && (
        <details style={{
          fontSize: '12px',
          color: '#6B7280'
        }}>
          <summary style={{
            cursor: 'pointer',
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            View Details
          </summary>
          <pre style={{
            background: 'white',
            padding: '12px',
            borderRadius: '6px',
            overflow: 'auto',
            fontSize: '11px',
            fontFamily: 'monospace'
          }}>
            {JSON.stringify(result.details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default FirebaseTest;
